const VIDEO_READY_TIMEOUT_MS = 12000;
const SUSPICIOUS_OBJECT_CLASSES = ['cell phone', 'remote', 'laptop'];
const HAVE_CURRENT_DATA = typeof HTMLMediaElement === 'undefined' ? 2 : HTMLMediaElement.HAVE_CURRENT_DATA;
const BLAZEFACE_URL = 'https://cdn.jsdelivr.net/npm/@tensorflow-models/blazeface@0.1.0/dist/blazeface.min.js';
const COCO_SSD_URL = 'https://cdn.jsdelivr.net/npm/@tensorflow-models/coco-ssd@2.2.3/dist/coco-ssd.min.js';
const TFJS_URL = 'https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.22.0/dist/tf.min.js';
const LOOK_AWAY_THRESHOLD = 0.18;
const LOOK_AWAY_DURATION_MS = 4000;
const REPEATED_GLANCE_WINDOW_MS = 12000;
const LINEAR_SCAN_WINDOW_MS = 7000;

let visionModulesPromise = null;

function getVideoTracks(videoElement) {
  const stream = videoElement?.srcObject;
  return typeof stream?.getVideoTracks === 'function' ? stream.getVideoTracks() : [];
}

function summarizeCameraState(videoElement) {
  const tracks = getVideoTracks(videoElement);
  return {
    hasStream: Boolean(videoElement?.srcObject),
    videoTracks: tracks.length,
    liveVideoTracks: tracks.filter((track) => track.readyState === 'live').length,
    enabledVideoTracks: tracks.filter((track) => track.enabled).length,
    readyState: videoElement?.readyState ?? 0,
    width: videoElement?.videoWidth ?? 0,
    height: videoElement?.videoHeight ?? 0,
  };
}

function hasUsableFrame(videoElement) {
  return Boolean(
    videoElement
    && videoElement.readyState >= HAVE_CURRENT_DATA
    && videoElement.videoWidth
    && videoElement.videoHeight
  );
}

function toPoint(value) {
  if (!value) return null;
  if (Array.isArray(value)) return [Number(value[0]), Number(value[1])];
  if (typeof value.arraySync === 'function') {
    const next = value.arraySync();
    return Array.isArray(next) ? [Number(next[0]), Number(next[1])] : null;
  }
  return null;
}

function getFaceBox(face) {
  const topLeft = toPoint(face?.topLeft || face?.box?.topLeft);
  const bottomRight = toPoint(face?.bottomRight || face?.box?.bottomRight);
  if (!topLeft || !bottomRight) return null;

  const width = Math.max(1, bottomRight[0] - topLeft[0]);
  const height = Math.max(1, bottomRight[1] - topLeft[1]);
  return {
    left: topLeft[0],
    top: topLeft[1],
    width,
    height,
    centerX: topLeft[0] + width / 2,
    centerY: topLeft[1] + height / 2,
  };
}

function estimateAttention(face) {
  const box = getFaceBox(face);
  const landmarks = Array.isArray(face?.landmarks) ? face.landmarks.map(toPoint).filter(Boolean) : [];
  if (!box || landmarks.length < 3) return null;

  const eyeA = landmarks[0];
  const eyeB = landmarks[1];
  const nose = landmarks[2];
  const eyesCenterX = (eyeA[0] + eyeB[0]) / 2;
  const eyesCenterY = (eyeA[1] + eyeB[1]) / 2;
  const horizontalOffset = (nose[0] - eyesCenterX) / box.width;
  const verticalOffset = (nose[1] - eyesCenterY) / box.height;

  let direction = 'center';
  if (horizontalOffset > LOOK_AWAY_THRESHOLD) {
    direction = 'right';
  } else if (horizontalOffset < -LOOK_AWAY_THRESHOLD) {
    direction = 'left';
  } else if (verticalOffset < 0.18) {
    direction = 'up';
  } else if (verticalOffset > 0.42) {
    direction = 'down';
  }

  return {
    direction,
    horizontalOffset: Number(horizontalOffset.toFixed(3)),
    verticalOffset: Number(verticalOffset.toFixed(3)),
    faceCenterX: Number(box.centerX.toFixed(1)),
    faceCenterY: Number(box.centerY.toFixed(1)),
  };
}

function stdDev(values) {
  if (!values.length) return 0;
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

function range(values) {
  if (!values.length) return 0;
  return Math.max(...values) - Math.min(...values);
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

async function loadBundledVisionModules() {
  const [tfModule, blazefaceModule, cocoSsdModule] = await Promise.all([
    import('@tensorflow/tfjs'),
    import('@tensorflow-models/blazeface'),
    import('@tensorflow-models/coco-ssd'),
  ]);

  return {
    tf: tfModule,
    blazeface: blazefaceModule.default?.load ? blazefaceModule.default : blazefaceModule,
    cocoSsd: cocoSsdModule.default?.load ? cocoSsdModule.default : cocoSsdModule,
    source: 'bundled',
  };
}

async function loadCdnVisionModules(bundledError) {
  await loadScript(TFJS_URL);
  await loadScript(BLAZEFACE_URL);
  await loadScript(COCO_SSD_URL);

  if (!window.tf || !window.blazeface || !window.cocoSsd) {
    throw new Error(`Vision models unavailable after CDN fallback. Bundled import failed: ${bundledError?.message || 'unknown error'}`);
  }

  return {
    tf: window.tf,
    blazeface: window.blazeface,
    cocoSsd: window.cocoSsd,
    source: 'cdn',
  };
}

async function loadVisionModules() {
  if (!visionModulesPromise) {
    visionModulesPromise = loadBundledVisionModules()
      .catch((bundledError) => loadCdnVisionModules(bundledError))
      .catch((error) => {
        visionModulesPromise = null;
        throw error;
      });
  }
  return visionModulesPromise;
}

async function prepareTfBackend(tf) {
  try {
    const webglReady = await tf.setBackend('webgl');
    if (!webglReady) throw new Error('WebGL backend unavailable');
  } catch {
    await tf.setBackend('cpu');
  }
  await tf.ready();
  return tf.getBackend();
}

export default class VisionProctorService {
  constructor({ onEvent } = {}) {
    this.onEvent = onEvent || (() => {});
    this.video = null;
    this.canvas = null;
    this.ctx = null;
    this.faceModel = null;
    this.objectModel = null;
    this.ready = false;
    this.running = false;
    this.lastFaceCount = 0;
    this.noFaceSince = 0;
    this.rafId = null;
    this.lastFaceRun = 0;
    this.lastObjectRun = 0;
    this.faceInFlight = false;
    this.objectInFlight = false;
    this.backend = '';
    this.tf = null;
    this.modelSource = '';
    this.lookAwaySince = 0;
    this.lookAwayDirection = '';
    this.attentionHistory = [];
    this.glanceHistory = [];
    this.lastAttentionDirection = 'center';
    this.lastGlanceAtByDirection = {};
    this.lastLinearScanAt = 0;
  }

  async start(videoElement) {
    if (!videoElement) return false;
    if (this.running) return this.ready;

    this.video = videoElement;
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });

    try {
      await this.waitForVideoFrame();
      const { tf, blazeface, cocoSsd, source } = await loadVisionModules();
      this.tf = tf;
      this.modelSource = source;
      this.backend = await prepareTfBackend(tf);
      this.faceModel = await blazeface.load({ maxFaces: 4 });
      this.objectModel = await cocoSsd.load();
      this.ready = true;
      this.running = true;
      this.loop();
      return true;
    } catch (error) {
      this.ready = false;
      this.running = false;
      const camera = summarizeCameraState(this.video);
      if (camera.hasStream || camera.videoTracks > 0 || this.modelSource) {
        this.onEvent('vision_unavailable', {
          error: error?.message || 'Vision models failed to load',
          modelSource: this.modelSource || 'none',
          camera,
        });
      }
      return false;
    }
  }

  waitForVideoFrame() {
    if (hasUsableFrame(this.video)) return Promise.resolve();

    return new Promise((resolve, reject) => {
      let intervalId;
      let timeoutId;

      const cleanup = () => {
        window.clearInterval(intervalId);
        window.clearTimeout(timeoutId);
        this.video?.removeEventListener('loadedmetadata', check);
        this.video?.removeEventListener('canplay', check);
        this.video?.removeEventListener('playing', check);
      };

      const finish = (callback, value) => {
        cleanup();
        callback(value);
      };

      const check = () => {
        if (hasUsableFrame(this.video)) {
          finish(resolve);
          return;
        }

        const state = summarizeCameraState(this.video);
        if (state.hasStream && state.videoTracks === 0) {
          finish(reject, new Error('No camera track available for vision proctoring'));
          return;
        }

        if (state.videoTracks > 0 && state.liveVideoTracks === 0) {
          finish(reject, new Error('Camera track ended before vision proctoring could start'));
        }
      };

      timeoutId = window.setTimeout(() => {
        finish(reject, new Error('Camera frame was not ready for vision proctoring'));
      }, VIDEO_READY_TIMEOUT_MS);

      intervalId = window.setInterval(check, 250);
      this.video?.addEventListener('loadedmetadata', check);
      this.video?.addEventListener('canplay', check);
      this.video?.addEventListener('playing', check);
      check();
    });
  }

  loop = () => {
    if (!this.running) return;
    const now = performance.now();
    if (hasUsableFrame(this.video)) {
      if (now - this.lastFaceRun >= 200) {
        this.lastFaceRun = now;
        this.runFaceDetection();
      }
      if (now - this.lastObjectRun >= 500) {
        this.lastObjectRun = now;
        this.runObjectDetection();
      }
    }
    this.rafId = requestAnimationFrame(this.loop);
  };

  drawFrame() {
    if (!hasUsableFrame(this.video) || !this.canvas || !this.ctx) return false;
    const width = this.video.videoWidth;
    const height = this.video.videoHeight;
    if (this.canvas.width !== width) this.canvas.width = width;
    if (this.canvas.height !== height) this.canvas.height = height;
    this.ctx.drawImage(this.video, 0, 0, width, height);
    return true;
  }

  async runFaceDetection() {
    if (this.faceInFlight || !this.faceModel) return;
    this.faceInFlight = true;

    try {
      if (!this.drawFrame()) return;
      const faces = await this.faceModel.estimateFaces(this.canvas, false);
      const faceCount = faces.length;
      this.lastFaceCount = faceCount;

      if (faceCount > 1) {
        this.onEvent('multiple_faces', { face_count: faceCount, model: 'blazeface', backend: this.backend, source: this.modelSource });
        this.noFaceSince = 0;
      } else if (faceCount === 0) {
        this.resetAttentionState();
        if (!this.noFaceSince) {
          this.noFaceSince = Date.now();
        } else if (Date.now() - this.noFaceSince >= 10000) {
          this.onEvent('no_face', { durationMs: Date.now() - this.noFaceSince, model: 'blazeface', backend: this.backend, source: this.modelSource });
          this.noFaceSince = Date.now();
        }
      } else {
        this.noFaceSince = 0;
        this.analyzeAttention(estimateAttention(faces[0]));
      }
    } catch {
      // Ignore intermittent inference failures caused by camera frame churn.
    } finally {
      this.faceInFlight = false;
    }
  }

  async runObjectDetection() {
    if (this.objectInFlight || !this.objectModel) return;
    this.objectInFlight = true;

    try {
      if (!this.drawFrame()) return;
      const predictions = await this.objectModel.detect(this.canvas);
      const suspicious = predictions.filter((item) => (
        SUSPICIOUS_OBJECT_CLASSES.includes(item.class) && item.score >= 0.6
      ));
      if (suspicious.length) {
        this.onEvent('phone_detected', {
          objects: suspicious.map((item) => ({ class: item.class, score: Number(item.score.toFixed(2)) })),
          model: 'coco-ssd',
          backend: this.backend,
          source: this.modelSource,
        });
      }
    } catch {
      // Ignore intermittent inference failures caused by camera frame churn.
    } finally {
      this.objectInFlight = false;
    }
  }

  resetAttentionState() {
    this.lookAwaySince = 0;
    this.lookAwayDirection = '';
    this.lastAttentionDirection = 'center';
  }

  analyzeAttention(attention) {
    if (!attention) return;

    const now = Date.now();
    const direction = attention.direction || 'center';
    this.attentionHistory = [
      ...this.attentionHistory.filter((sample) => now - sample.at < LINEAR_SCAN_WINDOW_MS),
      { at: now, ...attention },
    ];

    if (direction === 'center') {
      this.lookAwaySince = 0;
      this.lookAwayDirection = '';
    } else if (direction === this.lookAwayDirection) {
      if (this.lookAwaySince && now - this.lookAwaySince >= LOOK_AWAY_DURATION_MS) {
        this.onEvent('sustained_look_away', {
          direction,
          durationMs: now - this.lookAwaySince,
          horizontalOffset: attention.horizontalOffset,
          verticalOffset: attention.verticalOffset,
          model: 'blazeface-landmarks',
          source: this.modelSource,
        });
        this.lookAwaySince = now;
      }
    } else {
      this.lookAwayDirection = direction;
      this.lookAwaySince = now;
    }

    if ((direction === 'left' || direction === 'right') && direction !== this.lastAttentionDirection) {
      const lastGlanceAt = this.lastGlanceAtByDirection[direction] || 0;
      if (now - lastGlanceAt > 1200) {
        this.lastGlanceAtByDirection[direction] = now;
        this.glanceHistory = [
          ...this.glanceHistory.filter((sample) => now - sample.at < REPEATED_GLANCE_WINDOW_MS),
          { at: now, direction },
        ];
        const sameDirectionGlances = this.glanceHistory.filter((sample) => sample.direction === direction).length;
        if (sameDirectionGlances >= 4) {
          this.onEvent('repeated_side_glance', {
            direction,
            count: sameDirectionGlances,
            windowMs: REPEATED_GLANCE_WINDOW_MS,
            model: 'blazeface-landmarks',
            source: this.modelSource,
          });
          this.glanceHistory = this.glanceHistory.filter((sample) => sample.direction !== direction);
        }
      }
    }

    this.lastAttentionDirection = direction;
    this.detectLinearReadingPattern(now);
  }

  detectLinearReadingPattern(now) {
    if (now - this.lastLinearScanAt < 12000) return;

    const samples = this.attentionHistory.filter((sample) => now - sample.at < LINEAR_SCAN_WINDOW_MS);
    if (samples.length < 16) return;

    const horizontalValues = samples.map((sample) => sample.horizontalOffset);
    const verticalValues = samples.map((sample) => sample.verticalOffset);
    const horizontalRange = range(horizontalValues);
    const verticalStdDev = stdDev(verticalValues);
    const centerishSamples = samples.filter((sample) => ['center', 'left', 'right'].includes(sample.direction)).length;

    if (horizontalRange >= 0.1 && verticalStdDev <= 0.04 && centerishSamples / samples.length >= 0.85) {
      this.lastLinearScanAt = now;
      this.onEvent('linear_reading_pattern', {
        samples: samples.length,
        horizontalRange: Number(horizontalRange.toFixed(3)),
        verticalStdDev: Number(verticalStdDev.toFixed(3)),
        windowMs: LINEAR_SCAN_WINDOW_MS,
        model: 'blazeface-landmarks',
        source: this.modelSource,
      });
    }
  }

  isReady() {
    return this.ready;
  }

  stop() {
    this.running = false;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.faceModel?.dispose?.();
    this.objectModel?.dispose?.();
    this.video = null;
    this.canvas = null;
    this.ctx = null;
    this.faceModel = null;
    this.objectModel = null;
    this.ready = false;
    this.faceInFlight = false;
    this.objectInFlight = false;
    this.tf = null;
    this.modelSource = '';
    this.lookAwaySince = 0;
    this.lookAwayDirection = '';
    this.attentionHistory = [];
    this.glanceHistory = [];
    this.lastAttentionDirection = 'center';
    this.lastGlanceAtByDirection = {};
    this.lastLinearScanAt = 0;
  }
}
